import { Booking } from "@collins/react-componments/dist/types/booking";
import { QueryClient } from "@tanstack/react-query";
import {
  ViewBooking,
  ViewBookingAssignedAreasInner,
  ViewBookingStatusEnum
} from "collins-js-sdk";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import type { DiaryState } from "../store/slices/diarySlice";
import { BookingStagesEnum } from "../types/bookingEnums";
import { BookingV4 } from "../types/bookings";
import { VenueGroupConfig } from "../types/VenueGroup";
import {
  Areas,
  BookableArea,
  BookableAreaCombination,
  BookingOverride,
  BookingStage,
  BookingType,
  BookingZone,
  DateOpeningTimeOverride,
  OpeningTime,
  OpeningTimes,
  VenueConfig
} from "../types/venues";
import { WebsocketBookingUpdated } from "../types/websocketBookingUpdated";
import {
  getBookingsListKey,
  updateBookingsList
} from "../queries/bookingsListCache";
import {
  addOrUpdateBooking,
  editBooking,
  mapBookingToV4,
  mapBookingV4ToState
} from "./../APIs/Booking/UpdateBooking";
import {
  autoConfirmedFilter,
  collinsCallChanneFilter,
  customerRequestsFilter,
  doubleConfirmedFilter,
  notDoubleConfirmedFilter,
  runSheetNotesFilter
} from "./bookingFilters";
import { updateSelectedBookingCache } from "../queries/selectedBookingCache";
import { stageIs } from "./bookingStage";
import { hasBookingWriteInFlight } from "./bookingWriteTracker";
import { CompareProps, findSortedIndex, getDefinedProps } from "./helpers";
import { getWaitlistTimeString } from "./singleBooking";
import type { AppDispatch } from "../store/store";
import { selectedBookingMerged } from "../store/slices/diarySlice";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/**
 * Determines if a booking is on the same day
 * @param date1
 * @param date2
 * @returns
 */
export const isSameCollinsDay = (date1: Date, date2: Date): boolean =>
  dayjs(date1).isSame(dayjs(date2), "day");

/** True when the booking is on an earlier calendar day than `now`. */
export const isEarlierDay = (
  bookingDate: Date | string | null | undefined,
  now: Date
): boolean => {
  if (!bookingDate) return false;
  const date = new Date(bookingDate);
  return !isSameCollinsDay(now, date) && date.getTime() < now.getTime();
};

/** True when the booking is on a later calendar day than `now`. */
export const isLaterDay = (
  bookingDate: Date | string | null | undefined,
  now: Date
): boolean => {
  if (!bookingDate) return false;
  const date = new Date(bookingDate);
  return !isSameCollinsDay(now, date) && date.getTime() > now.getTime();
};

/**
 * Formats a time from 24 hour format to the locale format (e.g. 14:00 -> 2:00 PM)
 * @param time
 * @returns formattedTime
 */
export const formatTimeForLocale = (time: string, locale: string): string => {
  if (!time) {
    return "";
  }

  const [hours, mins] = time.split(":");

  if (!hours || !mins) {
    return "";
  }

  const parsedHours = parseInt(hours, 10);
  const parsedMins = parseInt(mins, 10);

  if (isNaN(parsedHours) || isNaN(parsedMins)) {
    return "";
  }

  const bookingTime = getTimeFromString(time);
  const dateTime = dayjs(bookingTime);

  if (locale === "en-US") {
    return dateTime.format("h:mm A");
  }

  return dateTime.format("HH:mm");
};

/**
 * Returns the appropriate Day.js time format string based on user locale.
 * Used for MobileTimeInput components to display times in the correct format.
 * Note: this returns a Day.js format string, not an Intl.DateTimeFormatOptions
 * object (unlike the *_OPTIONS constants in this file).
 * @param locale - The user's locale string (e.g., "en-US")
 * @returns Day.js format string: "h:mm A" for US locale (12-hour), "HH:mm" for others (24-hour)
 */
export const getTimeFormatString = (locale: string): string => {
  return locale === "en-US" ? "h:mm A" : "HH:mm";
};

/** Matches an "H:mm"/"HH:mm" 24-hour time string. */
const TIME_LABEL_PATTERN = /^\d{1,2}:\d{2}$/;

/**
 * Formats a label for the user's locale only when it is a bare time string
 * (e.g. "19:00"); non-time labels such as "Close", "ASAP" or "No time" are
 * returned unchanged.
 * @param label - The label to conditionally localise
 * @param locale - The user's locale string (e.g. "en-US")
 * @returns The locale-formatted time, or the original label when it is not a time
 */
export const formatTimeLabelForLocale = (
  label: string,
  locale: string
): string =>
  TIME_LABEL_PATTERN.test(label) ? formatTimeForLocale(label, locale) : label;

/**
 * Default Intl options producing a full "weekday day month year" date
 * (e.g. "Sat, 15 Jun 2025" for en-GB, "Sat, Jun 15, 2025" for en-US).
 */
export const LONG_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric"
};

/**
 * Intl options producing a numeric date (e.g. "15/06/2025" for en-GB,
 * "06/15/2025" for en-US).
 */
export const NUMERIC_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric"
};

/**
 * Intl options producing a compact weekday + numeric date with 2-digit year
 * (e.g. "Tue, 13/10/26" for en-GB, "Tue, 10/13/26" for en-US).
 * Designed for tight spaces like the booking details rail.
 */
export const COMPACT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  year: "2-digit"
};

/**
 * Intl options producing a short day/month date without a year
 * (e.g. "15 Jun" for en-GB, "Jun 15" for en-US).
 */
export const SHORT_DAY_MONTH_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short"
};

/**
 * Formats a date for the user's locale using Intl.DateTimeFormat, so day/month
 * ordering follows the locale (day-first for en-GB, month-first for en-US).
 *
 * Date-only strings (e.g. "2025-03-01") are parsed via Day.js so they resolve
 * to local midnight rather than UTC midnight, avoiding an off-by-one day shift
 * in negative-offset timezones.
 *
 * @param date - Date, Day.js input, or ISO/date string to format
 * @param locale - The user's locale string (e.g. "en-US")
 * @param options - Intl.DateTimeFormat options (defaults to LONG_DATE_OPTIONS)
 * @returns Locale-formatted date string, or "" if the input is invalid
 */
export const formatDateForLocale = (
  date: dayjs.ConfigType,
  locale: string,
  options: Intl.DateTimeFormatOptions = LONG_DATE_OPTIONS
): string => {
  if (date === undefined || date === null || date === "") {
    return "";
  }

  const parsed = dayjs(date);
  if (!parsed.isValid()) {
    return "";
  }

  return new Intl.DateTimeFormat(locale, options).format(parsed.toDate());
};

/**
 * Converts a 12-hour time string (e.g. "07:30 PM") to 24-hour format ("19:30").
 * Returns the input unchanged if it is already in 24-hour format.
 */
export const convertTimeTo24h = (time: string): string => {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i.exec(time);
  if (!match) return time;
  let hours = parseInt(match[1]);
  const minutes = match[2];
  const period = match[4].toUpperCase();
  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, "0")}:${minutes}`;
};

/**
 * Converts a string to a date (sets the day to today)
 * @param time
 * @returns
 */
export const getTimeFromString = (time: string): Date => {
  // Handle empty or invalid time strings
  if (!time || typeof time !== "string" || !time.includes(":")) {
    // Return current time as fallback
    return new Date();
  }

  const [hours, mins] = time.split(":");
  const parsedHours = parseInt(hours);
  const parsedMins = parseInt(mins);

  // Validate parsed values
  if (isNaN(parsedHours) || isNaN(parsedMins)) {
    // Return current time as fallback
    return new Date();
  }

  const parsedTime = dayjs().hour(parsedHours).minute(parsedMins).second(0);

  // Handle invalid dayjs objects
  if (!parsedTime.isValid()) {
    return new Date();
  }

  return parsedTime.toDate();
};

/**
 * Join firstname and lastname
 * @param firstName
 * @param lastName
 * @returns fullName
 */
export const getCustomerName = (booking: ViewBooking): string => {
  let name = "No Name";

  if (booking.walkIn) {
    name = "Walk In";
  }
  if (!!booking.firstName || !!booking.lastName) {
    name = `${booking?.firstName ?? ""} ${booking?.lastName ?? ""}`.trim();
  }

  return name;
};

/**
 * Filters bookings given a filter function (@see booking-filters)
 * @param bookings
 * @param filterFunction
 * @returns bookings
 */
export const filterBookings = (
  bookings: ViewBooking[] | undefined,
  filterFunction: any
): ViewBooking[] => bookings?.filter(filterFunction) ?? [];

export const bookingZone = (
  theAssignedAreas: Array<any>,
  bookingZoneFilters: Array<string>
): boolean => {
  for (let i = 0; i < theAssignedAreas.length; i++) {
    if (bookingZoneFilters.indexOf(theAssignedAreas[i].zone) > -1) {
      return true;
    }
  }

  return false;
};

export const bookingTimeSection = (
  time: string,
  timeOfTheDayFilters: Array<string>
): boolean => {
  if (!time) {
    return false;
  }

  let theTimeString = "";
  const theHour = parseInt(time.split(":")[0]);

  if (theHour >= 0 && theHour < 12) {
    theTimeString = "morning";
  } else if (theHour >= 12 && theHour < 16) {
    theTimeString = "afternoon";
  } else if (theHour >= 16) {
    theTimeString = "evening";
  }

  return timeOfTheDayFilters.indexOf(theTimeString) > -1;
};

export const bookingAttributesListFilter = (
  booking: ViewBooking,
  bookingAttributesFilters: Array<string>
) => {
  return (
    bookingAttributesFilters.find((currentBookingAttributeFilter) => {
      switch (currentBookingAttributeFilter) {
        case "autoConfirmed":
          return autoConfirmedFilter(booking);

        case "customerRequests":
          return customerRequestsFilter(booking);

        case "doubleConfirmed":
          return doubleConfirmedFilter(booking);

        case "runSheetNotes":
          return runSheetNotesFilter(booking);
        case "notDoubleConfirmed":
          return notDoubleConfirmedFilter(booking);

        case "collinsCall":
          return collinsCallChanneFilter(booking);
      }
    }) != undefined
  );
};

type BookingFilterStep = [
  Array<string>,
  (
    bookings: ViewBooking[],
    filters: Array<string>,
    sorted: ViewBooking[]
  ) => ViewBooking[]
];

export const getBookingList = (
  bookings: ViewBooking[] | undefined,
  timeFilters: Array<string>,
  zoneFilter: Array<string>,
  attributeFilter: Array<string>,
  typeFilter: Array<string>,
  labelFilter: Array<string> = []
): ViewBooking[] => {
  let filteredBookings = bookings;

  if (!filteredBookings || filteredBookings.length === 0) {
    return [];
  }

  const filterSteps: Array<BookingFilterStep> = [
    [timeFilters, timeSectionFilter],
    [typeFilter, bookingTypeFilter],
    [zoneFilter, bookingZoneFilter],
    [attributeFilter, bookingAttributeFilter],
    [labelFilter, bookingLabelFilter]
  ];

  for (const [filters, applyFilter] of filterSteps) {
    if (filters.length === 0) continue;
    filteredBookings = applyFilter(filteredBookings, filters, filteredBookings);
    if (filteredBookings.length === 0) return [];
  }

  return filteredBookings;
};

/**
 * Merge new bookings in to an existing set of bookings
 *
 * @param existingBookings
 * @param bookingsToMerge
 * @returns
 */
export const mergeBookings = (
  existingBookings: ViewBooking[] | undefined,
  bookingsToMerge: ViewBooking[] | undefined
): ViewBooking[] => {
  const updatedBookings: ViewBooking[] =
    existingBookings?.map((existingBooking) => {
      const bookingIndex = bookingsToMerge?.findIndex(
        ({ id }) => id === existingBooking.id
      );
      return bookingsToMerge && bookingIndex && bookingIndex > -1
        ? bookingsToMerge?.splice(bookingIndex, 1)[0]
        : (existingBooking ?? []);
    }) ?? [];

  return updatedBookings.concat(bookingsToMerge ?? []);
};

/**
 * Search for a booking using a term accorss fields
 * 'first_name', 'last_name', 'email', 'phone', 'reference'
 *
 * It also caters for "first_name last_name" e.g. "Alasdair Mackenzie"
 *
 * It will also return walk in bookings for the term 'walk in'
 *
 * @param term
 * @param bookings
 * @returns bookings
 */
export const filterBookingsForTerm = (
  bookings: ViewBooking[] | undefined,
  term: string
): ViewBooking[] => {
  if (!bookings) {
    return [];
  }

  if (term === "") {
    return bookings;
  }

  if ("walk in".startsWith(term.toLowerCase())) {
    return bookings.filter(
      (booking: any) => booking["walkIn"] || booking["walk_in"]
    );
  }

  return bookings.filter((booking: any) => {
    return [
      `${booking["first_name"]} ${booking["last_name"]}`,
      `${booking["first_name"]}`,
      `${booking["last_name"]}`,
      `${booking["firstName"]} ${booking["lastName"]}`,
      `${booking["firstName"]}`,
      `${booking["lastName"]}`,
      `${booking["email"]}`,
      `${booking["phone"]}`,
      `${booking["reference"]}`
    ].find((fieldValue: string) => {
      return fieldValue.toLowerCase().includes(term.toLowerCase());
    });
  });
};

/**
 * Get Booking Duration
 * @param startTime
 * @param endTime
 * @returns
 */
export const getBookingDuration = (startTime?: string, endTime?: string) => {
  if (!startTime || !endTime) {
    return 0;
  }

  const startDateTime = new Date(`${new Date().toDateString()} ${startTime}`);
  const endDateTime = new Date(`${new Date().toDateString()} ${endTime}`);

  let diff = (endDateTime.getTime() - startDateTime.getTime()) / 1000;
  diff /= 60;

  if (diff < 0) {
    diff = 0;
  }

  return Math.abs(Math.round(diff));
};

export const leftBookingStage: BookingStage = {
  id: BookingStagesEnum.Left,
  colour: "666666",
  label: "Left",
  walk_in_default: false
};

type GetBookingStagesProps = {
  venueConfig: VenueConfig;
  venueGroupConfig: VenueGroupConfig;
};
export const getBookingStages = ({
  venueConfig,
  venueGroupConfig
}: GetBookingStagesProps) => {
  let stages = venueGroupConfig.booking_stages || [];
  if (venueConfig?.override_booking_stages) {
    stages = venueConfig.booking_stages || [];
  }

  // Venues can already configure their own "left"-id stage; concat-ing the
  // synthetic one unconditionally would duplicate it (surfaced as a React
  // duplicate-key warning wherever stages are rendered by id).
  if (stages.some((stage) => stage.id === BookingStagesEnum.Left)) {
    return stages;
  }

  return stages.concat([leftBookingStage]);
};

// Reusable filter functions for runsheet filtering
export const filterActiveBookings = (
  bookings: ViewBooking[]
): ViewBooking[] => {
  // Filter out bookings that should never appear on runsheet
  // Left bookings are intentionally excluded - runsheet only shows active bookings
  return bookings.filter(
    (b) =>
      b.status !== "rejected" &&
      b.currentStage !== "no-show" &&
      b.currentStage?.toLowerCase() !== "left"
  );
};

/**
 * Runsheet table variant of `filterActiveBookings`: keeps left / no-show
 * bookings (they render under "Finished bookings") but still drops rejected.
 */
export const filterRunsheetTableBookings = (
  bookings: ViewBooking[]
): ViewBooking[] => bookings.filter((b) => b.status !== "rejected");

export const filterAwaitingArrivalBookings = (
  bookings: ViewBooking[]
): ViewBooking[] => {
  // Show bookings that are still awaiting arrival (before check-in)
  // This includes bookings with no stage or in "awaiting" stage
  // "Arrived" is the first status AFTER check-in, so it should NOT be included
  return bookings.filter(
    (b) => !b.currentStage || b.currentStage === "awaiting"
  );
};

export const filterCheckedInBookings = (
  bookings: ViewBooking[]
): ViewBooking[] => {
  // Show bookings that have checked in (any stage except awaiting/left/cancelled/rejected)
  // This includes "Arrived", "Dessert", "Bill", etc. - all post-check-in stages
  const excludedStages = ["awaiting", "cancelled", "rejected", "no-show"];

  return bookings.filter(
    (b) =>
      b.currentStage && !excludedStages.includes(b.currentStage.toLowerCase())
  );
};

export const filterBookingsByStage = (
  bookings: ViewBooking[],
  stageId: string
): ViewBooking[] => {
  // Filter bookings by specific stage ID
  return bookings.filter((b) => b.currentStage === stageId);
};

export const getExcludedStages = (): string[] => {
  // Define excluded stages that should not appear in filter dropdown
  return ["left", "cancelled", "rejected", "no-show"];
};

export const getNextBookingStageFromKey = (
  key: string,
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): BookingStage | null => {
  const stages = getBookingStages({
    venueConfig,
    venueGroupConfig
  });

  const indexOfCurrentStage = stages.findIndex(
    (stage: BookingStage) => stage.id === key
  );

  return stages[indexOfCurrentStage + 1] || stages[0];
};

export const walkingBookings = (
  key: string,
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): BookingStage | null => {
  const stages = getBookingStages({
    venueConfig,
    venueGroupConfig
  });

  return stages.find((s) => s.id === key) ?? null;
};

export const getBookingStageFromKey = (
  key: string,
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): BookingStage | null => {
  const stages = getBookingStages({
    venueConfig,
    venueGroupConfig
  });

  const indexOfCurrentStage = stages.findIndex(
    (stage: BookingStage) => stage.id === key
  );

  return stages[indexOfCurrentStage] || null;
};

/**
 * Get Zone Name From Zone Id
 * @param zoneId
 * @param zoneList
 * @returns zoneName
 */
export function getZoneNameFormZoneId(
  zoneId: string,
  zoneList: BookingZone[]
): string {
  let zoneName = "";
  if (zoneId && zoneList) {
    zoneList.forEach((list) => {
      if (list.id == zoneId) {
        zoneName = list.name ?? "";
      }
    });
  }

  return zoneName;
}
/**
 * Get Guest Count from booking list
 * @param bookingList
 * @returns totalNoOfGuest
 */
export function getGuestCount(bookingList: ViewBooking[]): number {
  let count = 0;

  bookingList.forEach((open) => {
    if (open.actualGuests) {
      count = count + open.actualGuests;
    }
  });

  return count;
}

export const MANDATORY_TABLE_MESSAGE =
  "Assign a table: this venue requires a table for every booking.";
export const MANDATORY_TABLE_UNASSIGN_MESSAGE =
  "This venue requires a table for every booking, so it can't be unassigned.";

export const isTableMandatory = (
  venueConfig?: Pick<VenueConfig, "mandatory_booking_table_enabled">
): boolean => venueConfig?.mandatory_booking_table_enabled === true;

export const hasAssignedTable = (
  booking?: Pick<ViewBooking, "assignedAreas">
): boolean => (booking?.assignedAreas?.length ?? 0) > 0;

// Waitlist entries are exempt because they never hold a table.
export const isMissingMandatoryTable = (
  booking: ViewBooking,
  venueConfig?: VenueConfig
): boolean =>
  isTableMandatory(venueConfig) &&
  !booking.waitlisted &&
  !hasAssignedTable(booking);

export const isMissingMandatoryTableOnEdit = (
  draft: ViewBooking,
  saved: ViewBooking | undefined,
  venueConfig?: VenueConfig
): boolean =>
  isMissingMandatoryTable(draft, venueConfig) &&
  (draft.walkIn === true || hasAssignedTable(saved));

/**
 * Checks if a booking has the basic information required
 * @param booking
 * @returns boolean
 */
export function isBookingValid(booking: ViewBooking) {
  return (
    !booking.date ||
    !booking.numPeople ||
    booking.numPeople === 0 ||
    !booking.type ||
    !booking.time ||
    !booking.duration ||
    booking.duration === 0
  );
}

export function isNonWalkInBookingValid(booking: ViewBooking) {
  return (
    !booking.firstName ||
    !booking.date ||
    !booking.numPeople ||
    booking.numPeople === 0 ||
    !booking.type ||
    (!booking.time && !booking.waitlisted) ||
    !booking.duration ||
    booking.duration === 0
  );
}

// Human-readable estimate for the "Add to waitlist" button, e.g. "1 hour 5 mins".
// Shared across the Add Booking form's waitlist actions.
export const formatWaitlistTime = (minutes: number): string => {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMins = minutes % 60;
    if (remainingMins === 0) {
      return hours === 1 ? "1 hour" : `${hours} hours`;
    }
    const hourText = hours === 1 ? "1 hour" : `${hours} hours`;
    const minText = remainingMins === 1 ? "1 min" : `${remainingMins} mins`;
    return `${hourText} ${minText}`;
  }
  return minutes === 1 ? "1 min" : `${minutes} mins`;
};

export function getAvailableAreaForBooking(
  booking: ViewBooking,
  bookableAreaList: BookableArea[],
  selectedType: BookingType | undefined
): BookableArea[] {
  const sortedTableList: BookableArea[] = [];
  if (!bookableAreaList || !booking.numPeople) {
    return sortedTableList;
  }
  const numOfPeopleCount = booking.numPeople;
  bookableAreaList.forEach((bookableArea) => {
    if (
      (bookableArea?.min_people ?? 0) <= numOfPeopleCount &&
      (bookableArea?.max_people ?? 0) >= numOfPeopleCount
    ) {
      if (selectedType?.areas) {
        selectedType.areas.forEach((selectedTypeArea: Areas) => {
          if (selectedTypeArea.id === bookableArea.id) {
            sortedTableList.push(bookableArea);
          }
        });
      } else {
        sortedTableList.push(bookableArea);
      }
    }
  });

  return sortedTableList;
}

/**
 * Get the end time for a booking, handling "until close" bookings (duration -1)
 * @param booking - The booking object
 * @param venueConfig - Optional venue configuration for opening times
 * @returns End time in HH:mm format
 */
export function getEndTime(
  booking: ViewBooking,
  venueConfig?: VenueConfig
): string {
  if (!booking?.date) {
    return "";
  }

  // Handle "until close" bookings (duration -1)
  if (booking.duration === -1) {
    if (venueConfig?.opening_times) {
      const bookingDate = new Date(booking.date);
      const { endMinutes } = getOpenCloseTimeInMinutes({
        date: bookingDate,
        openingTimes: venueConfig.opening_times,
        bookingTypes: venueConfig.booking_types
      });

      // Convert minutes to HH:mm format
      const hours = Math.floor(endMinutes / 60) % 24;
      const minutes = endMinutes % 60;
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    } else {
      // Fallback: if no venue config, return the start time
      return booking.time ?? "";
    }
  }

  const date = new Date(booking.date ?? "");
  const fromTime = new Date(`${date?.toDateString()} ${booking.time}`);
  const untilTime = booking.time
    ? dayjs(
        new Date(fromTime.getTime() + (booking.duration || 0) * 60000)
      ).format("HH:mm")
    : "";

  return untilTime;
}

/**
 * Calculate min/max time constraints from a booking's start time and duration.
 * Useful for constraining time pickers to the booking's time window.
 * @param booking - Object containing time (HH:mm format) and duration (minutes)
 * @returns Object with minTime (booking start) and maxTime (booking end) in HH:mm format
 */
/**
 * Checks if a string value contains valid note/description content.
 * Returns false for empty strings, whitespace-only strings, or the literal string "false"
 * which can appear in legacy data.
 * @param value - The string value to check
 * @returns true if the value contains valid content, false otherwise
 */
export const isValidNoteContent = (
  value: string | undefined | null
): boolean => {
  if (!value) return false;
  if (value === "false") return false;
  if (value.trim() === "") return false;
  return true;
};

export const getBookingTimeConstraints = (
  booking: { time?: string; duration?: number } | null | undefined
): { minTime: string | undefined; maxTime: string | undefined } => {
  if (!booking?.time || !booking?.duration) {
    return { minTime: undefined, maxTime: undefined };
  }

  const startTime = booking.time;
  const durationMinutes = booking.duration;

  // Parse start time (format: "HH:mm")
  const [startHours, startMinutes] = startTime.split(":").map(Number);
  if (isNaN(startHours) || isNaN(startMinutes)) {
    return { minTime: undefined, maxTime: undefined };
  }

  // Calculate end time
  const totalMinutes = startHours * 60 + startMinutes + durationMinutes;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;

  const minTime = `${startHours.toString().padStart(2, "0")}:${startMinutes.toString().padStart(2, "0")}`;
  const maxTime = `${endHours.toString().padStart(2, "0")}:${endMinutes.toString().padStart(2, "0")}`;

  return { minTime, maxTime };
};

export const bookingTypeFilter = (
  bookings: ViewBooking[],
  bookingTypeFilters: Array<string>,
  sortedBookingList: ViewBooking[]
) => {
  return sortedBookingList.length > 0
    ? sortedBookingList.filter((booking) => {
        if (booking.type?.id) {
          return bookingTypeFilters.indexOf(booking.type.id) > -1;
        }
      })
    : bookings.filter((booking) => {
        if (booking.type?.id) {
          return bookingTypeFilters.indexOf(booking.type.id) > -1;
        }
      });
};

export const bookingLabelFilter = (
  bookings: ViewBooking[],
  bookingLabelFilters: Array<string>,
  sortedBookingList: ViewBooking[]
) => {
  const bookingsToFilter =
    sortedBookingList.length > 0 ? sortedBookingList : bookings;

  return bookingsToFilter.filter((booking) =>
    booking.labels?.some((id) => bookingLabelFilters.includes(id))
  );
};

export const bookingZoneFilter = (
  bookings: ViewBooking[],
  bookingZoneFilters: Array<string>,
  sortedBookingList: ViewBooking[]
) => {
  return sortedBookingList.length > 0
    ? sortedBookingList.filter((booking) => {
        if (booking.assignedAreas) {
          return bookingZone(booking.assignedAreas, bookingZoneFilters);
        }
      })
    : bookings.filter((booking) => {
        if (booking.assignedAreas) {
          return bookingZone(booking.assignedAreas, bookingZoneFilters);
        }
      });
};

export const timeSectionFilter = (
  bookings: ViewBooking[],
  timeOfTheDayFilters: Array<string>,
  sortedBookingList: ViewBooking[]
) => {
  return sortedBookingList.length > 0
    ? sortedBookingList.filter((booking) => {
        return bookingTimeSection(booking.time ?? "", timeOfTheDayFilters);
      })
    : bookings.filter((booking) => {
        return bookingTimeSection(booking.time ?? "", timeOfTheDayFilters);
      });
};

export const bookingAttributeFilter = (
  bookings: ViewBooking[],
  timeOfTheDayFilters: Array<string>,
  sortedBookingList: ViewBooking[]
) => {
  return sortedBookingList.length > 0
    ? sortedBookingList.filter((booking) => {
        if (booking) {
          return bookingAttributesListFilter(booking, timeOfTheDayFilters);
        }
      })
    : bookings.filter((booking) => {
        if (booking) {
          return bookingAttributesListFilter(booking, timeOfTheDayFilters);
        }
      });
};

export const getZoneWiseAllArea = (
  bookingZoneId: string,
  bookingArea: Array<BookableArea>
) => {
  if (!bookingArea) {
    return [];
  }
  const areaList: BookableArea[] = [];
  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      areaList.push(bookingArea);
    }
  });

  return areaList;
};

export const getZoneWiseAllAreaCombine = (
  bookingZoneId: string,
  bookingArea: Array<BookableArea>,
  bookingAreaCombination: Array<BookableAreaCombination>
) => {
  if (!bookingAreaCombination) {
    return [];
  }
  const areaList: BookableAreaCombination[] = [];
  bookingAreaCombination.forEach(
    (bookingAreaCombination: BookableAreaCombination) => {
      bookingAreaCombination.area_ids.forEach((id: string) => {
        const bookableAreas = getBookableAreaFromAreaId(id, bookingArea);
        bookingArea.forEach(() => {
          if (bookingZoneId === bookableAreas.zone) {
            areaList.push(bookingAreaCombination);
          }
        });
      });
    }
  );
  const areaListCombine = areaList.filter(function (val, id, array) {
    return array.indexOf(val) === id;
  });
  return areaListCombine;
};

export const getNoZoneWiseAllAreaCombine = (
  bookingZoneId: string,
  bookingArea: Array<BookableArea>,
  bookingAreaCombination: Array<BookableAreaCombination>
) => {
  if (!bookingAreaCombination) {
    return [];
  }
  const areaList: BookableAreaCombination[] = [];
  bookingAreaCombination.forEach(
    (bookingAreaCombination: BookableAreaCombination) => {
      bookingAreaCombination.area_ids.forEach((id: string) => {
        const bookableAreas = getBookableAreaFromAreaId(id, bookingArea);
        bookingArea.forEach(() => {
          if (bookableAreas.zone === undefined) {
            areaList.push(bookingAreaCombination);
          }
        });
      });
    }
  );
  const areaListCombine = areaList.filter(function (val, id, array) {
    return array.indexOf(val) === id;
  });
  return areaListCombine;
};

export const getZoneWiseFreeArea = (
  bookingZoneId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  bookingTime: string,
  bookingEndTime: string
) => {
  if (!bookingArea) {
    return [];
  }
  const areaList: BookableArea[] = [];
  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      if (
        !checkAreaIsOccupiedOrNot(
          bookingArea.id,
          bookingList,
          bookingTime,
          bookingEndTime
        )
      ) {
        areaList.push(bookingArea);
      }
    }
  });

  return areaList;
};

export const getNoZoneWiseFreeArea = (
  areaId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  bookingTime: string,
  bookingEndTime: string
) => {
  if (!bookingArea) {
    return [];
  }
  const areaList: BookableArea[] = [];
  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingArea.zone === undefined) {
      if (
        !checkAreaIsOccupiedOrNot(
          bookingArea.id,
          bookingList,
          bookingTime,
          bookingEndTime
        )
      ) {
        areaList.push(bookingArea);
      }
    }
  });

  return areaList;
};

export const getZoneWiseFreeAreaCombine = (
  bookingZoneId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  bookingAreaCombination: Array<BookableAreaCombination>,
  bookingTime: string,
  bookingEndTime: string
) => {
  if (!bookingAreaCombination) {
    return [];
  }
  const areaList: BookableAreaCombination[] = [];
  bookingAreaCombination.forEach(
    (bookingAreaCombination: BookableAreaCombination) => {
      bookingAreaCombination.area_ids.forEach((id: string) => {
        const bookableAreas = getBookableAreaFromAreaId(id, bookingArea);

        if (bookingZoneId === bookableAreas.zone) {
          if (
            !checkAreaIsOccupiedOrNot(
              bookableAreas.id,
              bookingList,
              bookingTime,
              bookingEndTime
            )
          ) {
            areaList.push(bookingAreaCombination);
          }
        }
      });
    }
  );
  const areaListCombine = areaList.filter(function (val, id, array) {
    return array.indexOf(val) === id;
  });
  return areaListCombine;
};

export const getNoZoneWiseFreeAreaCombine = (
  bookingZoneId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  bookingAreaCombination: Array<BookableAreaCombination>,
  bookingTime: string,
  bookingEndTime: string
) => {
  if (!bookingAreaCombination) {
    return [];
  }
  const areaList: BookableAreaCombination[] = [];
  bookingAreaCombination.forEach(
    (bookingAreaCombination: BookableAreaCombination) => {
      bookingAreaCombination.area_ids.forEach((id: string) => {
        const bookableAreas = getBookableAreaFromAreaId(id, bookingArea);

        if (bookableAreas.zone === undefined) {
          if (
            !checkAreaIsOccupiedOrNot(
              bookableAreas.id,
              bookingList,
              bookingTime,
              bookingEndTime
            )
          ) {
            areaList.push(bookingAreaCombination);
          }
        }
      });
    }
  );
  const areaListCombine = areaList.filter(function (val, id, array) {
    return array.indexOf(val) === id;
  });
  return areaListCombine;
};

/**
 * Whether a booking holds its assigned areas during the given window: it is
 * active (new, in progress or confirmed), has not left and is not a no-show,
 * and its time overlaps the window.
 */
export const isBookingOccupyingSlot = (
  booking: ViewBooking,
  bookingTime: string,
  bookingEndTime: string
): boolean =>
  !!booking.assignedAreas?.length &&
  (booking.status === ViewBookingStatusEnum.Complete ||
    booking.status === ViewBookingStatusEnum.InProgress ||
    booking.status === ViewBookingStatusEnum.New) &&
  booking.currentStage !== "left" &&
  booking.actualGuests !== 0 &&
  bookingEndTime > (booking.time || "") &&
  getEndTime(booking) > bookingTime;

export const checkAreaIsOccupiedOrNot = (
  bookingAreaId: string,
  bookingList: ViewBooking[],
  bookingTime: string,
  bookingEndTime: string
) =>
  bookingList.some(
    (booking) =>
      isBookingOccupyingSlot(booking, bookingTime, bookingEndTime) &&
      booking.assignedAreas?.some(
        (area: ViewBookingAssignedAreasInner) =>
          area.id?.toString() === bookingAreaId
      )
  );

export const occupiedAreaEndTime = (
  bookingAreaId: string,
  bookingList: ViewBooking[]
) => {
  let untilTime = "";
  bookingList.forEach((booking: ViewBooking) => {
    if (
      booking.assignedAreas &&
      booking.assignedAreas.length &&
      (booking.status === ViewBookingStatusEnum.Complete ||
        booking.status === ViewBookingStatusEnum.InProgress ||
        booking.status === ViewBookingStatusEnum.New) &&
      booking.currentStage !== "left" &&
      booking.actualGuests !== 0
    ) {
      booking.assignedAreas.forEach((area: ViewBookingAssignedAreasInner) => {
        if (area.id?.toString() === bookingAreaId) {
          untilTime = getEndTime(booking);
        }
      });
      if (untilTime) {
        return untilTime;
      }
    }
  });

  return untilTime;
};

export const getBookableAreaFromAreaId = (
  bookingAreaId: string,
  bookingArea: Array<BookableArea>
): BookableArea => {
  let bookableArea = {} as BookableArea;
  if (!bookingArea) {
    return bookableArea;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingAreaId === bookingArea.id) {
      bookableArea = bookingArea;
    }
  });

  return bookableArea;
};

export const getBookableAreaListFromAreaId = (
  bookingAreaId: string,
  bookingArea: Array<BookableArea>
): Array<BookableArea> => {
  const bookableAreaList: BookableArea[] = [];
  if (!bookingArea) {
    return bookableAreaList;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingAreaId === bookingArea.id) {
      bookableAreaList.push(bookingArea);
    }
  });

  return bookableAreaList;
};

export const getAvailableCombinationAreaForBooking = (
  booking: ViewBooking,
  bookableAreaCombination: BookableAreaCombination[],
  bookings: ViewBooking[] | undefined
): BookableAreaCombination[] => {
  const sortedCombinedList: BookableAreaCombination[] = [];
  if (!bookableAreaCombination || !booking.numPeople || !bookings) {
    return sortedCombinedList;
  }
  const numberOfPeople = booking.numPeople;
  bookableAreaCombination.forEach(
    (areaCombination: BookableAreaCombination) => {
      if (
        areaCombination.max_people <= numberOfPeople &&
        areaCombination.max_people_seated &&
        areaCombination.max_people_seated >= numberOfPeople &&
        !checkJoinAreaIsOccupiedOrNot(
          areaCombination.area_ids,
          bookings,
          booking.time || "",
          getEndTime(booking)
        )
      ) {
        sortedCombinedList.push(areaCombination);
      }
    }
  );

  return sortedCombinedList;
};
export const getBookableAreaNameFromAreaId = (
  bookableAreaId: string,
  bookableArea: Array<BookableArea>
): string => {
  let areaName = "";
  if (!bookableArea) {
    return areaName;
  }
  bookableArea.forEach((bookingArea: BookableArea) => {
    if (bookableAreaId === bookingArea.id) {
      areaName = bookingArea.name || "";
    }
  });

  return areaName;
};

export const getAllAreaZoneWiseCount = (
  bookingZoneId: string,
  bookingArea: Array<BookableArea>
) => {
  let count = 0;
  if (!bookingArea) {
    return count;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      count = count + 1;
    }
  });

  return count;
};

export const getAllAreaNoZoneWiseCount = (bookingArea: Array<BookableArea>) => {
  let count = 0;
  if (!bookingArea) {
    return count;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingArea.zone === undefined) {
      count = count + 1;
    }
  });

  return count;
};

export const getFreeAreaZoneWiseCount = (
  bookingZoneId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  combinationAreaCount: number,
  bookingTime: string,
  bookingEndTime: string,
  bookingType?: BookingType
) => {
  let count = 0;
  if (!bookingArea) {
    return count;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      if (
        !checkAreaIsOccupiedOrNot(
          bookingArea.id,
          bookingList,
          bookingTime,
          bookingEndTime
        )
      ) {
        count = count + 1;
      }
    }
  });

  const areaList: BookableArea[] = [];
  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      if (bookingType?.areas) {
        bookingType?.areas.forEach((selectedTypeArea: Areas) => {
          if (selectedTypeArea.id === bookingArea.id) {
            areaList.push(bookingArea);
          }
        });
      } else {
        areaList.push(bookingArea);
      }
    }
  });

  return count;
};

export const getFreeAreaNoZoneWiseCount = (
  bookingZoneId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  combinationAreaCount: number,
  bookingTime: string,
  bookingEndTime: string,
  bookingType?: BookingType
) => {
  let count = 0;
  if (!bookingArea) {
    return count;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingArea.zone === undefined) {
      if (
        !checkAreaIsOccupiedOrNot(
          bookingArea.id,
          bookingList,
          bookingTime,
          bookingEndTime
        )
      ) {
        count = count + 1;
      }
    }
  });

  const areaList: BookableArea[] = [];
  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      if (bookingType?.areas) {
        bookingType?.areas.forEach((selectedTypeArea: Areas) => {
          if (selectedTypeArea.id === bookingArea.id) {
            areaList.push(bookingArea);
          }
        });
      } else {
        areaList.push(bookingArea);
      }
    }
  });

  return count;
};

export const getBookableAreaFromJoinTableId = (
  bookableArea: Array<BookableArea>,
  bookableAreaCombinationList?: BookableAreaCombination
): Array<BookableArea> => {
  const bookableAreaList: BookableArea[] = [];
  if (!bookableAreaCombinationList) {
    return bookableAreaList;
  }

  bookableAreaCombinationList.area_ids.forEach((id: string) => {
    const bookableAreas = getBookableAreaFromAreaId(id, bookableArea);
    if (bookableAreas.id) {
      bookableAreaList.push(bookableAreas);
    }
  });

  return bookableAreaList;
};

export const getAlreadyJoinAssignedAreaList = (
  bookableAreas: Array<BookableArea>,
  diaryModalBooking?: ViewBooking,
  bookingList?: ViewBooking[]
) => {
  const bookedAreaList: ViewBooking[] = [];

  // Check we have all the data we need to continue
  if (
    !bookableAreas ||
    !bookingList ||
    !diaryModalBooking ||
    !diaryModalBooking.date ||
    !diaryModalBooking.time ||
    !diaryModalBooking.duration ||
    !diaryModalBooking.assignedAreas ||
    diaryModalBooking.assignedAreas.length === 0
  ) {
    return [];
  }

  // set new booking start and end date ready for compare
  const newBookingFromTime = diaryModalBooking.time.split(":");
  const newBookingFrom = new Date(diaryModalBooking.date);
  newBookingFrom.setHours(
    parseInt(`${newBookingFromTime?.[0] ?? 0}`),
    parseInt(`${newBookingFromTime?.[1] ?? 0}`),
    0
  );
  const newBookingTo = new Date(
    newBookingFrom.getTime() + diaryModalBooking.duration * 60000
  );

  // reduce new booking area ids ready for compare
  const newBookingAreaIds = diaryModalBooking.assignedAreas.reduce(
    (ids, area) => {
      if (area.id) {
        ids.push(area.id.toString());
      }
      return ids;
    },
    [] as string[]
  );

  bookingList.forEach((booking: ViewBooking) => {
    // Check existing bookings aren't cancelled, already left or marked as a no show
    if (
      booking.status === ViewBookingStatusEnum.Rejected ||
      booking.status === ViewBookingStatusEnum.Deleted ||
      booking.status === ViewBookingStatusEnum.Lost ||
      booking.currentStage === "left" ||
      booking.actualGuests === 0
    ) {
      return [];
    }

    // Check existing bookings have all the data we need to compare
    if (
      !booking.date ||
      !booking.time ||
      !booking.duration ||
      !booking.assignedAreas
    ) {
      return [];
    }

    // Check if existing bookings are in the same area/table
    const matchedAreas = booking.assignedAreas.filter(
      (area) => area.id && newBookingAreaIds.includes(String(area.id))
    );
    if (matchedAreas.length === 0) {
      return [];
    }

    // Check new booking time + duration doesn't overlap existing bookings
    const bookingFromTime = booking.time.split(":");
    const bookingFrom = new Date(booking.date);
    bookingFrom.setHours(
      parseInt(`${bookingFromTime?.[0] ?? 0}`),
      parseInt(`${bookingFromTime?.[1] ?? 0}`),
      0
    );
    const bookingTo = new Date(
      bookingFrom.getTime() + booking.duration * 60000
    );

    const bookingTimesOverlap =
      bookingFrom <= newBookingTo && bookingTo >= newBookingFrom;
    if (!bookingTimesOverlap) {
      return [];
    }

    bookedAreaList.push(booking);
  });

  return bookedAreaList;
};

export const checkJoinAreaIsOccupiedOrNot = (
  joinAreasId: string[],
  bookingList: ViewBooking[],
  bookingTime: string,
  bookingEndTime: string
) => {
  let status = false;
  bookingList.forEach((booking: ViewBooking) => {
    if (
      booking.assignedAreas &&
      booking.assignedAreas.length &&
      (booking.status === ViewBookingStatusEnum.Complete ||
        booking.status === ViewBookingStatusEnum.InProgress ||
        booking.status === ViewBookingStatusEnum.New) &&
      booking.currentStage !== "left" &&
      booking.actualGuests !== 0 &&
      bookingEndTime > (booking.time || "") &&
      getEndTime(booking) > bookingTime &&
      joinAreasId
    ) {
      let count = 0;
      booking.assignedAreas.forEach(
        (area: ViewBookingAssignedAreasInner, index: number) => {
          if (area.id?.toString() === joinAreasId[index]) {
            count = count + 1;
            if (
              joinAreasId.length === booking.assignedAreas?.length &&
              count === booking.assignedAreas?.length
            ) {
              status = true;
            }
          }
        }
      );
      if (status) {
        return status;
      }
    }
  });

  return status;
};

export function isToTimeValid(booking: ViewBooking) {
  return (
    !booking.date ||
    !booking.numPeople ||
    booking.numPeople === 0 ||
    !booking.time
  );
}

type GetOpenCloseTimeInMinutesType = {
  date: Date;
  openingTimes?: OpeningTimes;
  bookingTypes?: BookingType[];
};
export type VenueStatus = "open" | "closed" | "private_hire";

type GetOpenCloseTimeInMinutesReturnType = {
  startMinutes: number;
  endMinutes: number;
  venueStatus: VenueStatus;
};

const timeStringToMinutes = (time: string): number | null => {
  if (!time?.includes(":")) return null;
  const parts = time.split(":");
  const hours = Number(parts[0]);
  const minutes = Number(parts[1] ?? 0);
  if (isNaN(hours) || isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

const toDateOnly = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const getOverridesForDate = (
  date: Date,
  bookingTypes?: BookingType[]
): BookingOverride[] => {
  if (!bookingTypes) {
    return [];
  }

  const dateTime = toDateOnly(date);
  const matches: BookingOverride[] = [];
  for (const bt of bookingTypes) {
    for (const override of bt.booking_overrides ?? []) {
      const dateFrom = toDateOnly(new Date(override.date_from));
      const dateTo = toDateOnly(new Date(override.date_to));
      if (
        dateTime >= dateFrom &&
        dateTime <= dateTo &&
        override.booking_available
      ) {
        matches.push(override);
      }
    }
  }
  return matches;
};

const weekdayKeys = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday"
] as const;

const formatDateKey = (date: Date): `${number}-${number}-${number}` => {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}` as `${number}-${number}-${number}`;
};

const getEarliestEffectiveRuleStart = (
  date: Date,
  openMinutes: number,
  bookingTypes?: BookingType[]
): number | null => {
  if (!bookingTypes) {
    return null;
  }

  const dayKey = weekdayKeys[date.getDay()];
  const dateTime = toDateOnly(date);
  let earliest: number | null = null;
  for (const bt of bookingTypes) {
    if (bt.archived) {
      continue;
    }
    const rule =
      bt.booking_overrides?.find(
        (override) =>
          dateTime >= toDateOnly(new Date(override.date_from)) &&
          dateTime <= toDateOnly(new Date(override.date_to))
      ) ?? bt.booking_rules?.[dayKey];
    if (!rule?.booking_available) {
      continue;
    }
    const from = timeStringToMinutes(rule.bookings_from);
    let to = timeStringToMinutes(rule.bookings_to);
    if (from === null || to === null || from === to) {
      continue;
    }
    if (to < from) {
      to += 24 * 60;
    }
    if (to <= openMinutes) {
      continue;
    }
    if (earliest === null || from < earliest) {
      earliest = from;
    }
  }
  return earliest;
};

const widenToOverrides = (
  date: Date,
  startMinutes: number,
  endMinutes: number,
  bookingTypes?: BookingType[]
): { startMinutes: number; endMinutes: number } => {
  for (const override of getOverridesForDate(date, bookingTypes)) {
    const overrideStart = timeStringToMinutes(override.bookings_from);
    let overrideEnd = timeStringToMinutes(override.bookings_to);

    if (overrideStart === null || overrideEnd === null) {
      continue;
    }

    if (overrideStart > overrideEnd) {
      overrideEnd += 24 * 60;
    }

    if (overrideEnd > endMinutes) {
      endMinutes = overrideEnd;
    }
  }

  return { startMinutes, endMinutes };
};

export const getOpeningTimeForDate = (
  date: Date,
  openingTimes: OpeningTimes
): OpeningTime | DateOpeningTimeOverride | undefined => {
  const dateKey = formatDateKey(date);
  const dateOverride = openingTimes[dateKey];
  if (dateOverride) {
    return dateOverride;
  }
  return openingTimes[weekdayKeys[date.getDay()]];
};

export const getVenueHours = (
  date: Date,
  openingTimes?: OpeningTimes
): { open: string; close: string } => {
  if (!openingTimes) {
    return { open: "00:00", close: "23:59" };
  }
  const openingTime = getOpeningTimeForDate(date, openingTimes);
  return {
    open: openingTime?.open ?? "00:00",
    close: openingTime?.close ?? "23:59"
  };
};

const resolveVenueStatus = (
  openingTime: OpeningTime | DateOpeningTimeOverride
): VenueStatus => {
  if (
    openingTime.status === "private_hire" ||
    openingTime.private_hire === true
  ) {
    return "private_hire";
  }
  if (!openingTime.status) {
    return "closed";
  }
  return "open";
};

export const getOpenCloseTimeInMinutes = ({
  date,
  openingTimes,
  bookingTypes
}: GetOpenCloseTimeInMinutesType): GetOpenCloseTimeInMinutesReturnType => {
  let startMinutes = 540;
  let endMinutes = 1320;

  if (!openingTimes) {
    return { startMinutes, endMinutes, venueStatus: "open" };
  }

  const openingTimeSelected = getOpeningTimeForDate(date, openingTimes);

  if (!openingTimeSelected) {
    return { startMinutes, endMinutes, venueStatus: "open" };
  }

  const venueStatus = resolveVenueStatus(openingTimeSelected);

  if (venueStatus === "closed") {
    return { startMinutes, endMinutes, venueStatus };
  }

  const startTime = openingTimeSelected.open
    ? openingTimeSelected.open.split(":")
    : ["12", "00"];
  const endTime = openingTimeSelected.close
    ? openingTimeSelected.close.split(":")
    : ["23", "00"];

  const startTimeMinutes = Number(startTime[1] ?? 0) / 60;
  const endTimeMinutes = Number(endTime[1] ?? 0) / 60;

  const startHour = Number(startTime[0]) + startTimeMinutes;
  let endHour = Number(endTime[0]) + endTimeMinutes;

  if (startHour > endHour) {
    endHour += 24;
  }

  startMinutes = startHour * 60;
  endMinutes = endHour * 60;

  const ruleStart = getEarliestEffectiveRuleStart(
    date,
    startMinutes,
    bookingTypes
  );
  if (ruleStart !== null && ruleStart < startMinutes) {
    startMinutes = ruleStart;
  }

  ({ startMinutes, endMinutes } = widenToOverrides(
    date,
    startMinutes,
    endMinutes,
    bookingTypes
  ));

  return { startMinutes, endMinutes, venueStatus };
};

export const getAreaWithoutZone = (
  venueConfig?: VenueConfig
): BookableArea[] => {
  const areas: BookableArea[] = [];
  if (venueConfig?.bookable_areas) {
    venueConfig.bookable_areas.forEach((bookableAreaList: BookableArea) => {
      if (bookableAreaList.zone === undefined) {
        areas.push(bookableAreaList);
      }
    });
  }
  return areas;
};

const isNoShowBooking = (booking: ViewBooking): boolean =>
  stageIs(booking, "no-show");

const isCancelledBooking = (booking: ViewBooking): boolean =>
  !isNoShowBooking(booking) &&
  (booking.status === ViewBookingStatusEnum.Rejected ||
    stageIs(booking, "cancelled"));

// Mirrors the shared BookingsGrid "hasBookingLeft" rule, excluding the
// cancelled/no-show bookings which have their own display toggles.
const isLeftBooking = (booking: ViewBooking): boolean =>
  !isCancelledBooking(booking) &&
  !isNoShowBooking(booking) &&
  (stageIs(booking, "left") || (booking.actualGuests ?? 0) === 0);

export type GridDisplayToggles = {
  displayLeft: boolean;
  displayCancelled: boolean;
  displayNoShow: boolean;
};

// Applies the diary grid's Left/Cancelled/No-Show display toggles, dropping
// bookings whose corresponding toggle is switched off before they reach the
// grid. When every toggle is on (the default) the list is returned unchanged.
export const filterBookingsForGridDisplay = (
  bookings: ViewBooking[] | undefined,
  { displayLeft, displayCancelled, displayNoShow }: GridDisplayToggles
): ViewBooking[] =>
  (bookings ?? []).filter((booking) => {
    if (!displayNoShow && isNoShowBooking(booking)) {
      return false;
    }
    if (!displayCancelled && isCancelledBooking(booking)) {
      return false;
    }
    if (!displayLeft && isLeftBooking(booking)) {
      return false;
    }
    return true;
  });

// The shared BookingsGrid styles and filters cancelled/no-show bookings by
// their `status` ("cancelled"/"no_show"), whereas Collins represents these via
// a rejected status or a currentStage. Normalise the status here so the grid
// can apply its distinct visual states and the displayCancelled/displayNoShow
// config toggles.
export const getBookingsForGrid = (bookings?: ViewBooking[]): Booking[] =>
  (bookings ?? []).map((booking) => {
    if (isNoShowBooking(booking)) {
      return { ...booking, status: "no_show" } as Booking;
    }
    if (isCancelledBooking(booking)) {
      return { ...booking, status: "cancelled" } as Booking;
    }
    return booking as Booking;
  });

export type DiaryListTarget = {
  date: Date;
  loadedVenueId?: DiaryState["loadedVenueId"];
};

export type BookingUpdateContext = DiaryListTarget & {
  selectedBooking?: DiaryState["selectedBooking"];
  queryClient: QueryClient;
  dispatch: AppDispatch;
  getCurrentContext?: () => DiaryListTarget;
};

export type BookingUpdateSnapshot = Omit<
  BookingUpdateContext,
  "getCurrentContext"
>;

/**
 * Maps a WebSocket booking payload to ViewBooking state.
 * The WebSocket payload is the full booking document in snake_case (same shape as BookingV4).
 * We cast it to Partial<BookingV4> and reuse the existing mapBookingV4ToState mapper.
 */
export const mapWebsocketBookingToState = (
  websocketBooking: WebsocketBookingUpdated
): ViewBooking & Partial<BookingV4> => {
  const asV4 = websocketBooking as unknown as Partial<BookingV4>;
  const mapped = mapBookingV4ToState(asV4);

  return {
    ...websocketBooking,
    ...mapped,
    id: websocketBooking._id,
    status: (websocketBooking.status as ViewBookingStatusEnum) ?? mapped.status
  } as ViewBooking & Partial<BookingV4>;
};

// A single change emits several WS echoes and they can arrive out of order. A
// delayed echo from an EARLIER change carries an older last_updated and would
// otherwise clobber a newer value already in the list — e.g. reverting a
// just-applied "Left" back to "Bill" on the grid card/popover, which reads to
// the user as "the stage didn't persist" even though the server saved it. The
// detail panel is immune because it refetches authoritatively on every echo, so
// only the list needs guarding. Only treat an echo as stale when both
// timestamps parse and the echo is STRICTLY older; missing/unparseable
// timestamps fall through to the merge so behaviour is unchanged when we can't
// judge freshness.
const isStaleEcho = (
  echoLastUpdated: WebsocketBookingUpdated["last_updated"],
  listLastUpdated: ViewBooking["lastUpdated"]
): boolean => {
  const echoTime = new Date(echoLastUpdated ?? "").getTime();
  const listTime = new Date(listLastUpdated ?? "").getTime();
  return (
    Number.isFinite(echoTime) &&
    Number.isFinite(listTime) &&
    echoTime < listTime
  );
};

const isSameCalendarDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

type WebsocketMergeResult = "applied" | "ignored";

/**
 * Applies one WebSocket payload to `working` IN PLACE. `working` must be a
 * copy owned by the caller's batch, never the list held in context, so a
 * batch allocates one array however many payloads it carries. Returns
 * "ignored" when the payload produces no write: a skipped field write (a
 * stale echo or an in-flight write guard) or an unplaceable payload — an
 * unknown id with no date, or a date that is not the diary's day.
 */
const applyWebsocketBookingToList = (
  working: ViewBooking[],
  websocketBooking: WebsocketBookingUpdated,
  diaryDate: Date
): WebsocketMergeResult => {
  const mapped = mapWebsocketBookingToState(websocketBooking);
  const definedFields = getDefinedProps(mapped);

  // A single change emits several WS echoes, and the follow-up ones are partial
  // — they omit current_stage. mapBookingV4ToState synthesises `currentStage: ""`
  // for an absent current_stage, and getDefinedProps keeps that "" (it only
  // strips undefined), so merging a partial echo would reset the booking's stage
  // to unchecked and silently undo a just-applied stage change. Honour this
  // function's "only overwrite fields actually present in the payload" contract
  // by dropping the synthesised default when the raw payload has no current_stage.
  if (websocketBooking.current_stage === undefined) {
    delete definedFields.currentStage;
  }
  // Same partial-echo problem for the guest count. mapBookingV4ToState fills a
  // missing actual_guests from num_people, and getDefinedProps keeps that
  // number, so a follow-up echo would paint the booked party size back over
  // an actual-guest count the list already holds.
  if (websocketBooking.actual_guests === undefined) {
    delete definedFields.actualGuests;
  }

  // mapBookingV4ToState always builds waitlistDetails ({ availableTime:
  // undefined } when the payload has none), and getDefinedProps keeps that
  // object, so a partial echo would wipe the booking's waitlist details. Keep
  // the "only overwrite fields present in the payload" contract.
  if (websocketBooking.waitlist_details === undefined) {
    delete definedFields.waitlistDetails;
  }

  const bookingId = websocketBooking._id;
  let found = false;
  let wrote = false;
  for (const [index, booking] of working.entries()) {
    if (booking.id !== bookingId) {
      continue;
    }
    found = true;
    // Drop an out-of-order echo that is older than what we already hold, so
    // it can't overwrite a newer stage/field in the list. See isStaleEcho.
    // Also drop it while a write to this booking is genuinely in flight
    // (bookingWriteTracker): the echo can predate the edit that write is
    // about to persist, so applying it would clobber the newer fields with
    // stale ones. While the device is offline a booking can carry a queued
    // `unsaved` edit no in-flight write covers yet — the same clobbering
    // applies, so the echo is dropped until the queued save replays. An
    // `unsaved` booking while online is NOT protected: that flag is set on
    // any online save failure too and only clears on a connection-status
    // transition, so keying on it alone would drop every later echo
    // (including other hosts' edits) indefinitely.
    if (
      !isStaleEcho(websocketBooking.last_updated, booking.lastUpdated) &&
      !hasBookingWriteInFlight(booking.id) &&
      !((booking as ViewBooking & editBooking).unsaved && !navigator.onLine)
    ) {
      working[index] = { ...booking, ...definedFields };
      wrote = true;
    }
    // Booking ids are unique, so no later entry can match.
    break;
  }
  if (found) {
    return wrote ? "applied" : "ignored";
  }

  if (
    !websocketBooking.date ||
    !isSameCalendarDay(new Date(diaryDate), new Date(websocketBooking.date))
  ) {
    return "ignored";
  }

  const newBooking = {
    actualGuests: websocketBooking.num_people ?? mapped.numPeople,
    ...definedFields,
    id: bookingId
  };
  const index = findSortedIndex({
    array: working,
    element: newBooking,
    compareFn
  });
  working.splice(index, 0, newBooking);
  return "applied";
};

/**
 * Merges an ordered batch of WebSocket payloads into the bookings list with
 * one commit. Every payload is applied in arrival order, never only the
 * latest per booking: follow-up echoes are partial, so keeping just the last
 * would drop fields that only an earlier echo carried. Only overwrites fields
 * actually present in each payload. Does NOT touch selectedBooking or
 * selectedBookingV4; TanStack Query invalidation owns those. A payload that
 * throws is skipped so the rest still land, and the first error is rethrown
 * after the cache write for the caller to report. The cache read, merge, and
 * write are synchronous, so the merge applies to the current list.
 *
 * Returns coarse applied/ignored counts across the batch, purely for the
 * caller's telemetry (see `emitDiaryUpdateMetric`). "applied" means the
 * payload wrote fields into the list; a skipped write (stale echo or
 * in-flight guard) counts as "ignored", so a batch where nothing was
 * written neither commits nor reports `applied`. Not split into
 * stale/unplaceable/unknown-id sub-reasons: `applyWebsocketBookingToList`
 * only ever returns "applied" | "ignored", so a finer split would mean
 * changing that return contract rather than reading it — out of scope for
 * telemetry alone.
 */
export const updateBookingsListFromWebsocketBatch = (
  websocketBookings: WebsocketBookingUpdated[],
  diaryContext: DiaryListTarget,
  queryClient: QueryClient
): { appliedCount: number; ignoredCount: number } => {
  const mergeInto = (bookings: ViewBooking[] | undefined) => {
    const working = [...(bookings ?? [])];
    let changed = false;
    let firstError: unknown;
    let appliedCount = 0;
    let ignoredCount = 0;

    for (const websocketBooking of websocketBookings) {
      try {
        const result = applyWebsocketBookingToList(
          working,
          websocketBooking,
          diaryContext.date
        );
        if (result === "applied") {
          changed = true;
          appliedCount += 1;
        } else {
          ignoredCount += 1;
        }
      } catch (error) {
        firstError ??= error;
      }
    }

    return { working, changed, appliedCount, ignoredCount, firstError };
  };

  // Writes target the passed context; DiaryPage supplies its live ref when the batch flushes.
  const key = getBookingsListKey(
    diaryContext.date,
    diaryContext.loadedVenueId ?? undefined
  );
  const currentBookings = queryClient.getQueryData<ViewBooking[]>(key);
  const merged = mergeInto(currentBookings);

  if (merged.changed) {
    updateBookingsList(queryClient, key, () => merged.working);
  }
  if (merged.firstError !== undefined) {
    throw merged.firstError;
  }

  // Counts come from the snapshot pass so they can be returned synchronously.
  return {
    appliedCount: merged.appliedCount,
    ignoredCount: merged.ignoredCount
  };
};

/**
 * Merges WebSocket booking data into the bookings list.
 * Only overwrites fields that are actually present (not undefined) in the WS payload.
 * Does NOT touch selectedBooking or selectedBookingV4 — those are handled
 * by TanStack Query invalidation.
 */
export const updateBookingsListFromWebsocket = (
  websocketBooking: WebsocketBookingUpdated,
  diaryContext: DiaryListTarget,
  queryClient: QueryClient
) =>
  updateBookingsListFromWebsocketBatch(
    [websocketBooking],
    diaryContext,
    queryClient
  );

export const updateStateBooking = (
  updatedBooking: ViewBooking,
  diaryContext: BookingUpdateContext
) => {
  const updatedBookingV4 = getDefinedProps(
    mapBookingToV4({ ...updatedBooking })
  );
  const updateBooking = getDefinedProps(
    mapBookingV4ToState({ ...updatedBookingV4 })
  );

  if (updatedBooking.currentStage === undefined) {
    updateBooking.currentStage = updatedBookingV4.current_stage = "";
  }

  const currentContext = diaryContext.getCurrentContext?.() ?? diaryContext;
  const key = getBookingsListKey(
    currentContext.date,
    currentContext.loadedVenueId ?? undefined
  );

  updateSelectedBookingCache(
    diaryContext.queryClient,
    updatedBookingV4?._id,
    (current) =>
      current ? ({ ...current, ...updatedBookingV4 } as BookingV4) : current
  );

  // Merge selected booking fields against the latest context at commit time.
  diaryContext.dispatch(
    selectedBookingMerged({
      ...updateBooking,
      id: updatedBooking.id
    })
  );

  updateBookingsList(diaryContext.queryClient, key, (current) => {
    let existingBooking = false;
    const bookings =
      current?.map((booking) => {
        if (booking.id === updatedBooking.id) {
          existingBooking = true;
          return { ...booking, ...updateBooking };
        }
        return booking;
      }) ?? [];
    if (!existingBooking) {
      if (!updatedBooking.date) return undefined;
      const diaryDate = new Date(currentContext.date);
      const updatedDate = new Date(updatedBooking.date);

      if (!isSameCalendarDay(diaryDate, updatedDate)) return undefined;

      // actualGuests is missing from websocket for new bookings
      // this is needed because we are treating bookings without actualGuests > 0 as "left"
      const newBooking = {
        actualGuests: updatedBooking.numPeople,
        ...updateBooking
      };
      const index = findSortedIndex({
        array: bookings,
        element: newBooking,
        compareFn
      });

      return [
        ...bookings.slice(0, index),
        newBooking,
        ...bookings.slice(index)
      ];
    }

    return bookings;
  });
};
export const updateFloorPlanBooking = async (
  updatedBooking: ViewBooking,
  diaryContext: BookingUpdateContext
) => {
  const key = getBookingsListKey(
    diaryContext.date,
    diaryContext.loadedVenueId ?? undefined
  );
  updateBookingsList(diaryContext.queryClient, key, (current) =>
    current?.map((booking) =>
      booking.id === updatedBooking.id
        ? { ...booking, ...updatedBooking }
        : booking
    )
  );

  await addOrUpdateBooking({
    booking: { ...updatedBooking, additionalAssignedAreas: null }
  });
};
const compareFn = ({
  originalObject,
  newObject
}: CompareProps<ViewBooking>) => {
  let arrayTime = parseInt(`${originalObject.time ?? ""}`.split(":").join(""));
  let newTime = parseInt(`${newObject.time ?? ""}`.split(":").join(""));

  if (
    newObject.waitlisted &&
    !newObject.currentStage &&
    newObject.waitlistDetails?.availableTime
  ) {
    newTime = parseInt(
      `${getWaitlistTimeString(newObject.waitlistDetails.availableTime)}`
        .split(":")
        .join("")
    );

    if (
      originalObject.waitlisted &&
      !originalObject.currentStage &&
      originalObject.waitlistDetails?.availableTime
    ) {
      arrayTime = parseInt(
        `${getWaitlistTimeString(originalObject.waitlistDetails.availableTime)}`
          .split(":")
          .join("")
      );
    }
  }

  if (newTime < arrayTime) {
    return -1;
  }

  if (newTime > arrayTime) {
    return 1;
  }

  return 0;
};

/**
 * Checks if a current time is within a specified time range
 * @param currentTime - Current time in HH:mm format
 * @param timeFrom - Start time in HH:mm format (optional)
 * @param timeTo - End time in HH:mm format (optional)
 * @returns true if within range or no range specified, false otherwise
 */
export const isWithinTimeRange = (
  currentTime: string,
  timeFrom?: string,
  timeTo?: string
): boolean => {
  if (!timeFrom || !timeTo) {
    return true;
  }
  return currentTime >= timeFrom && currentTime <= timeTo;
};

/**
 * Checks if an area is closed today based on weekday/time restrictions or date range
 * @param closedArea - The closure configuration object
 * @param currentDay - Current day in lowercase (e.g., "monday")
 * @param currentTime - Current time in HH:mm format
 * @param currentDate - Optional Dayjs object for date range checks
 * @returns true if the area is closed today, false otherwise
 */
export const isAreaClosedToday = (
  closedArea: any,
  currentDay: string,
  currentTime: string,
  currentDate?: dayjs.Dayjs
): boolean => {
  // Handle date range closures (with date_from and date_to)
  if (closedArea.date_from && closedArea.date_to && currentDate) {
    const dateFrom = dayjs(closedArea.date_from);
    const dateTo = dayjs(closedArea.date_to);

    // Check if current date is within the closure date range
    if (
      currentDate.isSameOrAfter(dateFrom, "day") &&
      currentDate.isSameOrBefore(dateTo, "day")
    ) {
      // If there are time restrictions, check them
      if (closedArea.time_from && closedArea.time_to) {
        return isWithinTimeRange(
          currentTime,
          closedArea.time_from,
          closedArea.time_to
        );
      }
      // If no time restrictions, the area is closed all day
      return true;
    }
    return false;
  }

  // Handle weekday-based closures (existing logic)
  if (closedArea.date_type !== "weekdays" || !closedArea.weekdays) {
    return false;
  }

  const isClosedToday =
    closedArea.weekdays[currentDay as keyof typeof closedArea.weekdays];
  if (!isClosedToday) {
    return false;
  }

  return isWithinTimeRange(
    currentTime,
    closedArea.time_from,
    closedArea.time_to
  );
};
